import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {UIGameMapCornerView} from './UIGameMapCornerView.ts'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils.ts'
import {Sound} from '../../Sound.ts'
import {ModeSettingEnum, VictoryPointStateReader} from '@colonist/game-base-types'
import {UIGameFloatingVictoryPointsController} from '../ui_game_floating_vp/UIGameFloatingVictoryPointsController.ts'
import {type BeginnerModeActionState} from '@colonist/game-state-types'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import {TileCornerBuildingType} from '@colonist/game-states'
import {type HexCorner, isCornerBuildingKnight, Point, type TileCorner} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import SoundType = Sound.SoundType

export class UIGameMapCornerController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager

    private readonly buildingViews: UIGameMapCornerView[] = []
    private readonly floatingVPController: UIGameFloatingVictoryPointsController

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        const drawingExistingCorners = true
        this.drawCorners(drawingExistingCorners)
        this.floatingVPController = new UIGameFloatingVictoryPointsController(this.uiGameManager)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.MapStateUpdated: this.drawCorners(data == false); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            default: break
        }
    }

    private drawCorners(drawingExistingCorners: boolean = false) {
        for(const corner of this.uiGameManager.gameState.mapState.tileState.tileCorners) {
            const previousBuildingType = this.getCornerView(corner.hexCorner)?.buildingType ?? undefined
            this.removePreviouslyDrawnCorner(corner)
            if(corner.buildingType == undefined) continue
            if(this.cornerExists(corner)) continue
            if(this.isDrawingPredefinedInitialPlacements()) drawingExistingCorners = true
            this.drawCorner(corner, previousBuildingType, drawingExistingCorners)
        }
    }

    private static playSoundForCorner(newBuildingType: TileCornerBuildingType, previousBuildingType: TileCornerBuildingType | undefined) {
        switch(newBuildingType) {
            case TileCornerBuildingType.Settlement: return Sound.playSound(previousBuildingType === TileCornerBuildingType.City ? SoundType.CityDestroy : SoundType.SettlementPlace)
            case TileCornerBuildingType.City: return Sound.playSound(SoundType.CityPlace)
            case TileCornerBuildingType.DestroyedCity: return Sound.playSound(SoundType.CityDestroy)
            default: return
        }
    }

    private cornerExists(corner: TileCorner): boolean {
        for(const oldCornerView of this.buildingViews) {
            if(oldCornerView.corner.sameCorner(corner)) return true
        }
        return false
    }

    private removePreviouslyDrawnCorner(tileCorner: TileCorner) {
        const oldCornerView = this.getCornerView(tileCorner.hexCorner)
        if(oldCornerView == undefined) return
        if(oldCornerView.buildingType == tileCorner.buildingType) return

        oldCornerView.parent.removeChild(oldCornerView)
        ArrayUtils.removeElementFromArray(this.buildingViews, oldCornerView)
    }

    private getCornerView(hexCorner: HexCorner): UIGameMapCornerView | undefined {
        for(const buildingView of this.buildingViews) {
            if(buildingView.corner.hexCorner.sameCorner(hexCorner)) return buildingView
        }
        return undefined
    }

    private drawCorner(corner: TileCorner, previousBuildingType: TileCornerBuildingType | undefined, drawingExistingCorners: boolean) {
        const texture = UIGameSpriteHandler.getCornerImage(corner)
        if(texture == undefined) return
        if(corner.buildingType == undefined) return

        const mapView = this.uiGameManager.mapController.mapView
        const center = corner.hexCorner.toPixel(mapView.mapCenter, mapView.hexagonHeight / 2)
        const buildingPoint = this.getPointForBuilding(corner.buildingType, center)
        if(buildingPoint == undefined) return
        const animationStartY = drawingExistingCorners ? buildingPoint.y : buildingPoint.y - mapView.hexagonHeight
        const buildingSize = this.getBuildingSize(corner.buildingType)
        const zIndex = isCornerBuildingKnight(corner.buildingType) ? mapView.layers.mapCornerKnightPieces : mapView.layers.mapCornerPieces
        const building = new UIGameMapCornerView(corner, buildingPoint.x, animationStartY, buildingSize, buildingSize, zIndex, texture, mapView)
        const hexCorner = new Point(building.x, buildingPoint.y)
        building.animationController.animateTo(hexCorner)
        this.buildingViews.push(building)

        this.addBuildingAnimationCompleteCallback(building, corner, previousBuildingType, drawingExistingCorners)
        this.uiGameManager.mapController.mapView.sortChildren()
    }

    private addBuildingAnimationCompleteCallback(building: UIGameMapCornerView, corner: TileCorner, previousBuildingType: TileCornerBuildingType | undefined, drawingExistingCorners: boolean) {
        if(drawingExistingCorners) return
        if(corner.buildingType == previousBuildingType) return

        building.animationController.animationCompleteCallBack = () => {
            if(corner.buildingType == undefined) return
            UIGameMapCornerController.playSoundForCorner(corner.buildingType, previousBuildingType)
            this.floatingVPController.createFloatingVPForCorner(corner, previousBuildingType)
        }
    }

    private getBuildingSize(cornerBuildingType: TileCornerBuildingType): number {
        switch(cornerBuildingType) {
            case TileCornerBuildingType.Settlement:
            case TileCornerBuildingType.DestroyedCity:
            case TileCornerBuildingType.City: return this.uiGameManager.mapController.mapView.cornerCircleR * 2
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3: return this.uiGameManager.mapController.mapView.cornerCircleR * 1.6
        }
    }

    private getPointForBuilding(cornerBuildingType: TileCornerBuildingType, cornerCenter: Point): Point {
        switch(cornerBuildingType) {
            case TileCornerBuildingType.Settlement:
            case TileCornerBuildingType.DestroyedCity:
            case TileCornerBuildingType.City: return this.getPointForCityAndSettlement(cornerCenter)
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3: return this.getPointForKnight(cornerCenter)
        }
    }

    private getPointForCityAndSettlement(cornerCenter: Point): Point {
        const x = cornerCenter.x - this.uiGameManager.mapController.mapView.cornerCircleR
        const y = cornerCenter.y - this.uiGameManager.mapController.mapView.cornerCircleR * 1.3
        return new Point(x, y)
    }

    private getPointForKnight(cornerCenter: Point): Point {
        const knightRadius = this.uiGameManager.mapController.mapView.cornerCircleR * 1.6
        const x = cornerCenter.x - knightRadius / 2
        const y = cornerCenter.y - knightRadius / 2
        return new Point(x, y)
    }

    private isDrawingPredefinedInitialPlacements(): boolean {
        const modesWithPredefinedInitialPlacements = [ModeSettingEnum.BeginnerModeClassic4P]
        const currentModeSetting = this.uiGameManager.gameState.gameModeData.modeSetting
        if(!modesWithPredefinedInitialPlacements.includes(currentModeSetting)) return false

        const currentTurnPlayer = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        const isPredefinedInitialPlacementForCurrentTurnPlayer = VictoryPointStateReader.totalPrivateVictoryPoints(currentTurnPlayer.victoryPointsState) <= 1
        return isPredefinedInitialPlacementForCurrentTurnPlayer
    }

    private onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        if(data.hiddenUIElements.includes(HiddenUIElement.HowToWinFloatingVP)) this.floatingVPController.hideHowToWinFloatingVPView()
    }
}
